import { useState } from "react";

interface FilterRows<T extends string> {
    rows: T[];
    addRow: (filterType: T) => void;
    removeRow: (filterType: T) => void;
    /** Swaps the row at `index` for another type, clearing the one it replaces. */
    replaceRow: (filterType: T, index: number) => void;
    /**
     * Drops every row without clearing values — the caller resets them in one
     * go, and per-row clears would clobber that reset.
     */
    clearRows: () => void;
}

/**
 * Tracks which filter rows a filter panel shows. A row exists for every filter
 * that has a value, plus any row the user added but hasn't filled in yet.
 */
export function useFilterRows<T extends string>(
    initialRows: () => T[],
    clearFilter: (filterType: T) => void
): FilterRows<T> {
    const [rows, setRows] = useState<T[]>(initialRows);

    return {
        rows,
        addRow: (filterType) =>
            setRows((prev) =>
                prev.includes(filterType) ? prev : [...prev, filterType]
            ),
        removeRow: (filterType) => {
            setRows((prev) => prev.filter((row) => row !== filterType));
            clearFilter(filterType);
        },
        replaceRow: (filterType, index) => {
            const replaced = rows[index];
            if (replaced === filterType) {
                return;
            }
            setRows((prev) =>
                prev.map((row, i) => (i === index ? filterType : row))
            );
            clearFilter(replaced);
        },
        clearRows: () => setRows([]),
    };
}
